import React, { useContext, useEffect, useMemo, useState } from "react";
import SalaryDetail from "../../FinancialReport/Salary/SalaryDetail/SalaryDetail";
import { UserContext } from "../../../contexts/CurrentUserContext";
import {
  getCompanySalaryApprovalDetailV2,
  patchCompanySalaryApprovalV2,
  patchSalaryApprovalStepV2,
  TIMESHEET_STATUSES,
} from "../../../api/timesheetApi";
import FinansistPushActions from "../../FinancialReport/Salary/SalaryDetail/FinansistPushActions";
import { hasFinansistPushRole } from "../../../utils/finansistPayrollPush";
import "./SalaryApprovalDetail.css";

const formatPersonShortName = (person) => {
  if (!person) return "-";
  const surname = person.surname || "";
  const firstInitial = person.name?.[0] ? `${person.name[0]}.` : "";
  return `${surname} ${firstInitial}`.trim() || "-";
};

const normalizeStatus = (status) => {
  switch ((status || "").toLowerCase()) {
    case "approved":
    case "согласован":
      return "Согласован";
    case "rejected":
    case "declined":
    case "отклонено":
    case "не согласован":
      return "Отклонено";
    case "pending":
    case "на рассмотрении":
    default:
      return "На рассмотрении";
  }
};

const CompanyApprovalDetail = ({ approvalId, department, onBack }) => {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDepartments, setExpandedDepartments] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [showConfirmApprove, setShowConfirmApprove] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectCommentError, setRejectCommentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!approvalId) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getCompanySalaryApprovalDetailV2(approvalId);
        setDetail(data);
      } catch (e) {
        console.error(e);
        setError(e.message || "Ошибка при загрузке");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [approvalId]);

  const approvalStatus = normalizeStatus(
    detail?.approval?.status || department?.status,
  );
  const canAct = approvalStatus === "На рассмотрении";
  const rows = detail?.table_data?.rows || [];

  // Серверные поля `detail.total_*` иногда приходят как `0`, хотя суммы по строкам
  // отделений корректные. Поэтому считаем итоги локально по `rows`, чтобы UI
  // всегда отражал реальные значения таблицы.
  const computedCompanyTotals = useMemo(() => {
    const totalSalary = rows.reduce(
      (sum, row) => sum + Number(row?.total_salary || 0),
      0,
    );
    const totalPaid = rows.reduce(
      (sum, row) => sum + Number(row?.paid || 0),
      0,
    );
    const totalRemainder = rows.reduce(
      (sum, row) => sum + Number(row?.remainder || 0),
      0,
    );
    return { totalSalary, totalPaid, totalRemainder };
  }, [rows]);

  const periodLabel = useMemo(() => {
    const year = detail?.table_data?.year ?? detail?.approval?.year;
    const month = detail?.table_data?.month ?? detail?.approval?.month;
    if (!year || !month) return "-";
    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return `${months[Math.max(0, Number(month) - 1)]} ${year}`;
  }, [
    detail?.approval?.month,
    detail?.approval?.year,
    detail?.table_data?.month,
    detail?.table_data?.year,
  ]);

  const submitDecision = async (status, comment = "") => {
    if (!approvalId) return;
    setIsSubmitting(true);
    try {
      await patchCompanySalaryApprovalV2(approvalId, { status, comment });
      if (onBack) await onBack();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Не удалось обновить статус согласования.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading)
    return <div className="salary-approval-detail__loading">Загрузка...</div>;
  if (error)
    return (
      <div className="salary-approval-detail__loading">Ошибка: {error}</div>
    );
  if (!detail)
    return <div className="salary-approval-detail__loading">Нет данных</div>;

  return (
    <div className="salary-detail">
      <div className="salary-detail__header">
        <button className="salary-detail__back-button" onClick={onBack}>
          <span className="salary-detail__back-arrow">←</span>
          Согласование зарплаты
        </button>
      </div>

      <div className="salary-detail__summary">
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">Период:</span>
          <span className="salary-detail__summary-value">{periodLabel}</span>
        </div>
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">
            Общая сумма к выплате:
          </span>
          <span className="salary-detail__summary-value salary-detail__summary-value--amount">
            {new Intl.NumberFormat("ru-RU").format(
              computedCompanyTotals.totalSalary || 0,
            )}{" "}
            тг
          </span>
        </div>
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">Выплачено:</span>
          <span className="salary-detail__summary-value salary-detail__summary-value--amount">
            {new Intl.NumberFormat("ru-RU").format(
              computedCompanyTotals.totalPaid || 0,
            )}{" "}
            тг
          </span>
        </div>
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">Остаток:</span>
          <span className="salary-detail__summary-value salary-detail__summary-value--amount">
            {new Intl.NumberFormat("ru-RU").format(
              computedCompanyTotals.totalRemainder || 0,
            )}{" "}
            тг
          </span>
        </div>
        <div className="salary-detail__summary-item">
          <span className="salary-detail__summary-label">Отправил:</span>
          <span className="salary-detail__summary-value">
            {formatPersonShortName(detail?.approval?.initiator)}
          </span>
        </div>
      </div>

      <div className="salary-detail__table-container">
        <table className="salary-detail__table salary-detail__table--collapsed-sections">
          <thead>
            <tr>
              <th>Отделение</th>
              <th>Количество сотрудников</th>
              <th>Заработная плата</th>
              <th>Выплачено</th>
              <th>Остаток</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isDeptOpen = Boolean(
                expandedDepartments[row.department_id],
              );
              return (
                <React.Fragment key={row.department_id}>
                  <tr className="salary-detail__table-row salary-detail__group-row">
                    <td className="salary-detail__group-cell">
                      <button
                        type="button"
                        className="salary-detail__group-toggle"
                        onClick={() =>
                          setExpandedDepartments((prev) => ({
                            ...prev,
                            [row.department_id]: !prev[row.department_id],
                          }))
                        }
                      >
                        <span className="salary-detail__group-chevron">
                          {isDeptOpen ? "▼" : "▶"}
                        </span>
                        <span className="salary-detail__group-title">
                          {row.department_name}
                        </span>
                      </button>
                    </td>
                    <td>{row.employees_count || 0}</td>
                    <td>
                      {new Intl.NumberFormat("ru-RU").format(
                        row.total_salary || 0,
                      )}{" "}
                      тг
                    </td>
                    <td>
                      {new Intl.NumberFormat("ru-RU").format(row.paid || 0)} тг
                    </td>
                    <td>
                      {new Intl.NumberFormat("ru-RU").format(
                        row.remainder || 0,
                      )}{" "}
                      тг
                    </td>
                  </tr>
                  {isDeptOpen &&
                    (row.groups || []).map((group) => {
                      const groupKey = `${row.department_id}-${group.group_id}`;
                      const isGroupOpen = Boolean(expandedGroups[groupKey]);
                      return (
                        <React.Fragment key={groupKey}>
                          <tr className="salary-detail__table-row">
                            <td style={{ paddingLeft: "28px" }}>
                              <button
                                type="button"
                                className="salary-detail__group-toggle"
                                onClick={() =>
                                  setExpandedGroups((prev) => ({
                                    ...prev,
                                    [groupKey]: !prev[groupKey],
                                  }))
                                }
                              >
                                <span className="salary-detail__group-chevron">
                                  {isGroupOpen ? "▼" : "▶"}
                                </span>
                                <span className="salary-detail__group-title">
                                  {group.group_name}
                                </span>
                              </button>
                            </td>
                            <td>{group.employees_count || 0}</td>
                            <td>
                              {new Intl.NumberFormat("ru-RU").format(
                                group.total_salary || 0,
                              )}{" "}
                              тг
                            </td>
                            <td>
                              {new Intl.NumberFormat("ru-RU").format(
                                group.paid || 0,
                              )}{" "}
                              тг
                            </td>
                            <td>
                              {new Intl.NumberFormat("ru-RU").format(
                                group.remainder || 0,
                              )}{" "}
                              тг
                            </td>
                          </tr>
                          {isGroupOpen &&
                            (group.employees || []).map((employee) => (
                              <tr
                                key={`${groupKey}-${employee.user_id}`}
                                className="salary-detail__table-row salary-detail__employee-row"
                              >
                                <td style={{ paddingLeft: "56px" }}>
                                  {employee.full_name}
                                </td>
                                <td></td>
                                <td>
                                  {new Intl.NumberFormat("ru-RU").format(
                                    employee.total_salary || 0,
                                  )}{" "}
                                  тг
                                </td>
                                <td>
                                  {new Intl.NumberFormat("ru-RU").format(
                                    employee.paid || 0,
                                  )}{" "}
                                  тг
                                </td>
                                <td>
                                  {new Intl.NumberFormat("ru-RU").format(
                                    employee.remainder || 0,
                                  )}{" "}
                                  тг
                                </td>
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}
            <tr className="salary-detail__table-row salary-detail__table-row--total">
              <td className="salary-detail__total-label">ИТОГО</td>
              <td></td>
              <td>
                {new Intl.NumberFormat("ru-RU").format(
                  computedCompanyTotals.totalSalary || 0,
                )}{" "}
                тг
              </td>
              <td>
                {new Intl.NumberFormat("ru-RU").format(
                  computedCompanyTotals.totalPaid || 0,
                )}{" "}
                тг
              </td>
              <td>
                {new Intl.NumberFormat("ru-RU").format(
                  computedCompanyTotals.totalRemainder || 0,
                )}{" "}
                тг
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {canAct && (
        <div className="salary-detail__actions">
          <button
            className="salary-detail__button salary-detail__button--reset"
            onClick={() => setShowRejectModal(true)}
            disabled={isSubmitting}
            style={{
              backgroundColor: "#fff",
              color: "#ff4d4f",
              border: "1px solid #ff4d4f",
            }}
          >
            Отклонить
          </button>
          <button
            className="salary-detail__button salary-detail__button--submit"
            onClick={() => setShowConfirmApprove(true)}
            disabled={isSubmitting}
          >
            Согласовать
          </button>
        </div>
      )}

      {showConfirmApprove && (
        <div
          className="popup-overlay"
          onClick={() => setShowConfirmApprove(false)}
        >
          <div
            className="popup popup-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="popup-text">
              Подтвердить согласование зарплаты компании?
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowConfirmApprove(false)}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={() => submitDecision(TIMESHEET_STATUSES.APPROVED)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Отправка..." : "Согласовать"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div
          className="popup-overlay"
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className="popup popup-reject"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="popup-reject__title">
              Отклонение зарплаты компании
            </h3>
            <div className="popup-reject__comment-section">
              <label className="popup-reject__label">
                Комментарий <span className="popup-reject__required">*</span>
              </label>
              <textarea
                className={`popup-reject__textarea ${
                  rejectCommentError ? "popup-reject__textarea--error" : ""
                }`}
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value);
                  if (rejectCommentError) setRejectCommentError("");
                }}
                rows={4}
              />
              {rejectCommentError && (
                <span className="popup-reject__error">
                  {rejectCommentError}
                </span>
              )}
            </div>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowRejectModal(false)}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm"
                onClick={() => {
                  if (!rejectComment.trim()) {
                    setRejectCommentError(
                      "Комментарий обязателен для заполнения",
                    );
                    return;
                  }
                  submitDecision(
                    TIMESHEET_STATUSES.REJECTED,
                    rejectComment.trim(),
                  );
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Отправка..." : "Отклонить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SalaryApprovalDetail = ({ department, onBack }) => {
  const { userData } = useContext(UserContext);
  const [showConfirmApprove, setShowConfirmApprove] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectCommentError, setRejectCommentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const currentUserPendingStep = useMemo(() => {
    if (!userData?.id || !department?.approvers?.length) return null;
    return department.approvers.find(
      (a) =>
        Number(a.approverId) === Number(userData.id) &&
        a.status === "На рассмотрении",
    );
  }, [department?.approvers, userData?.id]);

  const isFullyApproved = useMemo(() => {
    if (!department?.approvers?.length) return false;
    return department.approvers.every((a) => a.status === "Согласован");
  }, [department?.approvers]);

  const canPushToFinansist = useMemo(
    () =>
      hasFinansistPushRole(userData) &&
      isFullyApproved &&
      Boolean(department?.approvalId),
    [department?.approvalId, isFullyApproved, userData],
  );

  const handleConfirmApprove = async () => {
    const stepId = currentUserPendingStep?.stepId;
    if (!stepId) {
      window.alert("Не найден шаг согласования для текущего пользователя.");
      return;
    }
    setIsSubmitting(true);
    try {
      await patchSalaryApprovalStepV2(stepId, {
        status: TIMESHEET_STATUSES.APPROVED,
        comment: "",
      });
      setShowConfirmApprove(false);
      if (onBack) onBack();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Не удалось согласовать. Попробуйте ещё раз.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReject = async () => {
    if (!rejectComment.trim()) {
      setRejectCommentError("Комментарий обязателен для заполнения");
      return;
    }
    const stepId = currentUserPendingStep?.stepId;
    if (!stepId) {
      window.alert("Не найден шаг согласования для текущего пользователя.");
      return;
    }
    setIsSubmitting(true);
    try {
      await patchSalaryApprovalStepV2(stepId, {
        status: TIMESHEET_STATUSES.REJECTED,
        comment: rejectComment.trim(),
      });
      setShowRejectModal(false);
      setRejectComment("");
      setRejectCommentError("");
      if (onBack) onBack();
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Не удалось отклонить. Попробуйте ещё раз.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (department?.isCompanyApproval) {
    return (
      <CompanyApprovalDetail
        approvalId={department.approvalId}
        department={department}
        onBack={onBack}
      />
    );
  }

  return (
    <div>
      <SalaryDetail
        department={department}
        onBack={onBack}
        initialMonth={department?.date}
        readOnlyMode
        approvalId={department?.approvalId}
        backButtonLabel="Согласование зарплаты"
        customActions={
          currentUserPendingStep || canPushToFinansist ? (
            <>
              {currentUserPendingStep && (
                <>
                  <button
                    className="salary-detail__button salary-detail__button--reset"
                    onClick={() => setShowRejectModal(true)}
                    disabled={isSubmitting}
                    style={{
                      backgroundColor: "#fff",
                      color: "#ff4d4f",
                      border: "1px solid #ff4d4f",
                    }}
                  >
                    Отклонить
                  </button>
                  <button
                    className="salary-detail__button salary-detail__button--submit"
                    onClick={() => setShowConfirmApprove(true)}
                    disabled={isSubmitting}
                  >
                    Согласовать
                  </button>
                </>
              )}
              <FinansistPushActions
                approvalId={department?.approvalId}
                calculationYear={department?.date?.getFullYear()}
                month={
                  department?.date ? department.date.getMonth() + 1 : undefined
                }
                visible={canPushToFinansist}
              />
            </>
          ) : null
        }
      />

      {showConfirmApprove && (
        <div
          className="popup-overlay"
          onClick={() => setShowConfirmApprove(false)}
        >
          <div
            className="popup popup-confirm"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="popup-text">
              Вы уверены, что хотите согласовать зарплату для отдела "
              {department?.name}"?
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowConfirmApprove(false)}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm popup-button-confirm--approve"
                onClick={handleConfirmApprove}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Отправка..." : "Согласовать"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div
          className="popup-overlay"
          onClick={() => setShowRejectModal(false)}
        >
          <div
            className="popup popup-reject"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="popup-reject__title">Отклонение зарплаты</h3>
            <p className="popup-reject__subtitle">
              Вы уверены, что хотите отклонить зарплату для отдела "
              {department?.name}"?
            </p>
            <div className="popup-reject__comment-section">
              <label className="popup-reject__label">
                Комментарий <span className="popup-reject__required">*</span>
              </label>
              <textarea
                className={`popup-reject__textarea ${
                  rejectCommentError ? "popup-reject__textarea--error" : ""
                }`}
                value={rejectComment}
                onChange={(e) => {
                  setRejectComment(e.target.value);
                  if (rejectCommentError) setRejectCommentError("");
                }}
                placeholder="Укажите причину отклонения..."
                rows={4}
              />
              {rejectCommentError && (
                <span className="popup-reject__error">
                  {rejectCommentError}
                </span>
              )}
            </div>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={() => setShowRejectModal(false)}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm"
                onClick={handleConfirmReject}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Отправка..." : "Отклонить"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SalaryApprovalDetail;
